import type {FollowAccountType} from "@buildwithflux/following-schema";
import Button from "@mui/material/Button";
import {useCallback} from "react";

import {writeFollow} from "../../api/relationshipApi";
import {useViewer} from "../../context/ViewerContext";
import {useFollowState} from "../../hooks/useRelationship";
import {gotoSignUp} from "../../utils/auth";

export default function FollowButton(props: {
    accountUid: string;
    accountType: FollowAccountType;
    onFollowChange?: (following: boolean) => void;
}) {
    const {accountUid, accountType, onFollowChange} = props;
    const {uid: viewerUid, authResolved} = useViewer();
    const {active, setOptimistic} = useFollowState(accountUid, viewerUid);

    const isSelf = viewerUid != null && viewerUid === accountUid;
    const following = active === true;

    const onClick = useCallback(() => {
        if (!viewerUid) {
            gotoSignUp();
            return;
        }
        const next = !following;
        setOptimistic(next);
        onFollowChange?.(next);
        void writeFollow(next ? "follow" : "unfollow", accountUid, accountType).catch(() => {
            setOptimistic(!next);
            onFollowChange?.(!next);
        });
    }, [viewerUid, following, setOptimistic, onFollowChange, accountUid, accountType]);

    // Anonymous viewers can always click through to sign-up; signed-in viewers
    // wait for the realtime state to resolve before the button is interactive.
    const disabled = isSelf || (viewerUid != null && active === undefined) || (viewerUid == null && !authResolved);

    return (
        <Button
            onClick={onClick}
            variant="outlined"
            fullWidth
            color="primary"
            size="small"
            disabled={disabled}
            data-cy="followProfileButton"
        >
            {following ? "Following" : "Follow"}
        </Button>
    );
}
